(function () {
  "use strict";

  // bump this when you change logic, so we can safely re-init
  const AIC_VERSION = "2026-02-07-v3";

  // ---------------- Helpers ----------------
  const prefersReducedMotion = () =>
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const isMobile = () =>
    window.matchMedia && window.matchMedia("(max-width: 989px)").matches;

  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  const safeMarquee = ($el, action) => {
    try {
      $el.marquee(action);
    } catch (e) {
      // silent
    }
  };

  const getSectionTargets = (sectionRoot) => {
    if (sectionRoot) return [sectionRoot];
    return Array.from(document.querySelectorAll(".aic-section"));
  };

  const getVariantFromSection = (sectionEl) => {
    const root =
      sectionEl.querySelector(".aic--right") ||
      sectionEl.querySelector(".aic--background") ||
      sectionEl.querySelector(".aic--bottom");

    if (!root) return "unknown";
    if (root.classList.contains("aic--right")) return "right";
    if (root.classList.contains("aic--background")) return "background";
    if (root.classList.contains("aic--bottom")) return "bottom";
    return "unknown";
  };

  const readSlidesCount = (marqueeEl) => {
    // in liquid: <div class='aic__marquee content' data-slides='{{ slide_count }}'>
    const v = marqueeEl && marqueeEl.dataset ? parseInt(marqueeEl.dataset.slides || "0", 10) : 0;
    return Number.isFinite(v) && v > 0 ? v : 0;
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  // ---------------- Speed model ----------------
  // We want similar "px/sec feel" across columns.
  // Since duration is "time per loop", and loop distance grows with more slides,
  // we scale duration with slides count, but keep it bounded.

  const getPerSlideMs = ({ variant, mobile }) => {
    // Lower per-slide ms => faster movement.
    // Background was "too slow" visually: give it a more aggressive per-slide baseline.
    if (mobile) {
      if (variant === "background") return 2200;
      if (variant === "right") return 2400;
      return 2600; // bottom/other
    } else {
      if (variant === "background") return 2400;
      if (variant === "right") return 2600;
      return 2900;
    }
  };

  const getVerticalDuration = ({ variant, mobile, slidesCount }) => {
    const perSlide = getPerSlideMs({ variant, mobile });

    // If data-slides isn't present, fallback to conservative values:
    if (!slidesCount) {
      if (mobile) {
        if (variant === "background") return 26000;
        if (variant === "right") return 28000;
        return 30000;
      }
      if (variant === "background") return 30000;
      if (variant === "right") return 32000;
      return 36000;
    }

    // Scale duration with slides count (so longer columns don't feel "stuck")
    // Then clamp to keep CPU stable and avoid crazy slow/fast loops.
    const raw = slidesCount * perSlide; // ms
    if (mobile) return clamp(raw, 16000, 52000);
    return clamp(raw, 18000, 60000);
  };

  const getHorizontalSpeed = (w) => {
    if (w >= 1440) return Math.round(w * 0.05);
    if (w >= 1200) return Math.round(w * 0.055);
    if (w >= 990) return Math.round(w * 0.06);
    if (w >= 750) return Math.round(w * 0.07);
    if (w >= 576) return Math.round(w * 0.075);
    return Math.round(w * 0.08);
  };

  // ---------------- Init one section ----------------
  const initAicSection = (sectionEl) => {
    if (!sectionEl || !window.jQuery) return;

    const $ = window.jQuery;
    const variant = getVariantFromSection(sectionEl);

    const verticalMarquees = Array.from(sectionEl.querySelectorAll(".aic__marquee"));
    const horizontal = sectionEl.querySelector(".aic__marquee-horizontal");
    const wrapper = sectionEl.querySelector(".aic__marquees");

    // Reduced motion: destroy existing marquees & stop.
    if (prefersReducedMotion()) {
      verticalMarquees.forEach((m) => safeMarquee($(m), "destroy"));
      if (horizontal) safeMarquee($(horizontal), "destroy");
      return;
    }

    // If this section was previously initialized with another version,
    // rebuild to ensure speed changes apply.
    const needsRebuild = sectionEl.__aicVersion !== AIC_VERSION;
    sectionEl.__aicVersion = AIC_VERSION;

    // ---------- Vertical marquees ----------
    verticalMarquees.forEach((marqueeEl, i) => {
      const $m = $(marqueeEl);

      // If already initialized and not rebuilding -> skip
      const alreadyInit = !!marqueeEl.querySelector(".js-marquee-wrapper");
      if (alreadyInit && !needsRebuild) return;

      // If rebuilding: destroy first
      if (alreadyInit && needsRebuild) safeMarquee($m, "destroy");

      const direction = i % 2 === 0 ? "down" : "up";
      const slidesCount = readSlidesCount(marqueeEl);

      const duration = getVerticalDuration({
        variant,
        mobile: isMobile(),
        slidesCount,
      });

      $m.marquee({
        delayBeforeStart: 0,
        direction,
        allowCss3Support: true,
        startVisible: true,
        duplicated: true,
        duration,
        gap: 0,
        pauseOnHover: true,
      });
    });

    // ---------- Horizontal marquee ----------
    if (horizontal) {
      const $h = $(horizontal);
      const alreadyInit = !!horizontal.querySelector(".js-marquee-wrapper");

      const initialW =
        (wrapper && wrapper.getBoundingClientRect().width) ||
        document.documentElement.clientWidth;

      const marqueeSettings = {
        delayBeforeStart: 0,
        direction: "left",
        allowCss3Support: true,
        startVisible: true,
        duplicated: true,
        speed: getHorizontalSpeed(initialW),
        gap: 0,
        pauseOnHover: true,
      };

      if (!alreadyInit) {
        $h.marquee(marqueeSettings);
      } else if (needsRebuild) {
        safeMarquee($h, "destroy");
        $h.marquee(marqueeSettings);
      }

      // Resize: only if wrapper exists, debounced, ignore tiny changes
      if (wrapper && "ResizeObserver" in window) {
        if (!sectionEl.__aicResizeObserverAttached) {
          sectionEl.__aicResizeObserverAttached = true;

          let lastW = Math.round(initialW);

          const rebuild = debounce((w) => {
            const rounded = Math.round(w);
            if (Math.abs(rounded - lastW) < 12) return;
            lastW = rounded;

            safeMarquee($h, "destroy");
            marqueeSettings.speed = getHorizontalSpeed(rounded);
            $h.marquee(marqueeSettings);
          }, 300);

          new ResizeObserver((entries) => {
            const w = entries[0].contentRect.width;
            rebuild(w);
          }).observe(wrapper);
        }
      }
    }

    // ---------- Pause when offscreen (performance win) ----------
    if ("IntersectionObserver" in window) {
      if (sectionEl.__aicObserverAttached) return;
      sectionEl.__aicObserverAttached = true;

      const $all = [];
      verticalMarquees.forEach((m) => $all.push($(m)));
      if (horizontal) $all.push($(horizontal));

      const setPaused = (paused) => {
        $all.forEach(($el) => {
          if (!$el || !$el.length) return;
          safeMarquee($el, paused ? "pause" : "resume");
        });
      };

      const io = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          const visible = entry && entry.isIntersecting && entry.intersectionRatio > 0.08;
          setPaused(!visible);
        },
        { threshold: [0, 0.08, 0.2, 0.5] }
      );

      io.observe(sectionEl);

      // extra: tab hidden = pause
      document.addEventListener("visibilitychange", () => {
        setPaused(document.hidden);
      });
    }
  };

  const initAll = (sectionRoot) => {
    const targets = getSectionTargets(sectionRoot);
    targets.forEach((t) => initAicSection(t));
  };

  // 1) Direct
  initAll();

  // 2) Shopify editor compat
  document.addEventListener("shopify:section:load", (e) => initAll(e.target));
  document.addEventListener("shopify:section:reorder", (e) => initAll(e.target));
})();
